/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Request-parameter catalog (#851) — the context catalog that drives the rebuilt
 * ParameterEditor's autocomplete, add-recommended, and won't-populate validation
 * (guidance §2). Mirrors `ortb-field-catalog.ts` / `response-field-catalog.ts`
 * for the outbound URL-parameter surface.
 *
 * **VAST-first (guidance §14).** VAST demand sources append these as URL query
 * params; the driver resolves each `defaultMacro` through the shared context
 * resolver. OpenRTB POSTs its endpoint verbatim and carries device/user/consent
 * in the *body* — so its param catalog is intentionally empty; the rare
 * SSP-specific URL param (e.g. a Prebid `account_id`) is added by hand.
 *
 * `defaultMacro` values use the tokens the canonical resolver actually accepts
 * (`{ua}`, `{ip}`, `{device_type}` — snake_case), verified against
 * `context-value-resolver.ts`.
 *
 * @see DemandSourceMethod — the one protocol vocabulary (#936).
 */

import { AUTO_POPULATED_VAST_PARAM_NAMES } from '@auto-populated-params';
import type { DestinationField } from '@destination-field';
import type { DemandSourceMethod } from '../../../types';

/**
 * **A VAST query parameter is a destination**, and its declaration is the shared one (`#1163`,
 * `D34`). `ParamCatalogField` and `ParamImportance` are gone: the importance enum was identical to
 * `OrtbFieldImportance` member for member, and `defaultMacro` was `D32`'s `derived` case spelled
 * one destination at a time.
 *
 * Every one of the eighteen entries below is `derived` — which is exactly why nothing here could
 * describe `pubid` or an endpoint, and why those had to be special-cased elsewhere.
 */
export type ParamCatalogField = DestinationField;

/**
 * Standard VAST request parameters, most-important first.
 *
 * **`type` is declared on every entry that has a knowable one** (`#1191`). It was declared on
 * none until 2026-08-28, against every one of `ORTB_FIELD_CATALOG`'s fields (101 then) — both have been `DestinationField`
 * since `#1163` and only one side was ever populated. `#914` called its type check *"pure win —
 * the data exists"*, which was true of ORTB and false here: shipping the checker without this
 * would have given a validator that always passes on half a parity-required pair, and a validator
 * that always passes reads as *this is fine*.
 *
 * These are read as **text** (`DestinationEncoding`), because a query string has one
 * representation for everything: `w=1080` is the string `"1080"` and is exactly right. What a
 * `number` here rules out is `1080px` and `yes`.
 */
/** Standard VAST request parameters, most-important first. */
export const VAST_PARAM_CATALOG: ReadonlyArray<ParamCatalogField> = [
  {
    name: 'ip',
    valueType: 'string',
    importance: 'required',
    description: 'User IP address',
    value: { from: 'derived', source: 'ip' },
  },
  {
    name: 'ua',
    valueType: 'string',
    importance: 'required',
    description: 'User-agent string',
    value: { from: 'derived', source: 'ua' },
  },
  {
    name: 'w',
    valueType: 'number',
    importance: 'recommended',
    description: 'Player width (px)',
    value: { from: 'derived', source: 'w' },
  },
  {
    name: 'h',
    valueType: 'number',
    importance: 'recommended',
    description: 'Player height (px)',
    value: { from: 'derived', source: 'h' },
  },
  {
    name: 'url',
    valueType: 'string',
    importance: 'recommended',
    description: 'Page / app URL the ad runs on',
    value: { from: 'derived', source: 'url' },
  },
  // App identity (#960). **Required on a CTV-first platform**: exchanges price, target and
  // fraud-check on the bundle, and several require the store URL. Neither catalog offered
  // any of the three, so an operator was never told to map the fields that identify whose
  // inventory this is — the real SpringServe tag in `ad-tag.adapter.spec.ts` carries all
  // three, which is what the SSPs plainly expect.
  {
    name: 'app_bundle',
    valueType: 'string',
    importance: 'required',
    description: 'App bundle / package ID',
    value: { from: 'derived', source: 'app_bundle' },
  },
  {
    name: 'app_name',
    valueType: 'string',
    importance: 'recommended',
    description: 'App display name',
    value: { from: 'derived', source: 'app_name' },
  },
  {
    name: 'app_store_url',
    valueType: 'string',
    importance: 'recommended',
    description: 'App store listing URL',
    value: { from: 'derived', source: 'app_store_url' },
  },
  {
    name: 'gdpr',
    valueType: 'number',
    importance: 'optional',
    description: 'GDPR-applies flag (0/1)',
    value: { from: 'derived', source: 'gdpr' },
  },
  {
    name: 'gdpr_consent',
    valueType: 'string',
    importance: 'optional',
    description: 'GDPR TC consent string',
    value: { from: 'derived', source: 'gdpr_consent' },
  },
  {
    name: 'us_privacy',
    valueType: 'string',
    importance: 'optional',
    description: 'US privacy / CCPA string',
    value: { from: 'derived', source: 'us_privacy' },
  },
  {
    name: 'dnt',
    valueType: 'number',
    importance: 'optional',
    description: 'Do-not-track flag (0/1)',
    value: { from: 'derived', source: 'dnt' },
  },
  {
    name: 'lmt',
    valueType: 'number',
    importance: 'optional',
    description: 'Limit-ad-tracking flag (0/1)',
    value: { from: 'derived', source: 'lmt' },
  },
  /**
   * **The two numbers that define a pod, on a proxy whose job is building pods** (#949).
   *
   * Neither was offered, so an operator was never prompted for the constraints that decide
   * what the exchange is asked to fill. The cost is measured: a live `demo` source had lost
   * its duration parameter during ordinary editing, the mock ad server fell back to its own
   * 180s default against a 150s pod, and the baseline read **96.7% fill** — above the mock's
   * own maximum band, with nothing anywhere flagging the missing parameter.
   *
   * `recommended` rather than `optional` on purpose: that is what puts them in
   * *add-recommended* and in won't-populate validation, which is the whole point of
   * surfacing them. Not `required`, because not every exchange accepts them.
   *
   * **They are different kinds and the descriptions have to say so** (#949): slots are a
   * count, duration is seconds. `pod_ads` and `pod_max_dur` look parallel and are not.
   */
  {
    name: 'pod_max_dur',
    valueType: 'number',
    importance: 'recommended',
    description: 'Maximum total pod duration (seconds)',
    value: { from: 'derived', source: 'pod_max_duration' },
  },
  {
    name: 'pod_ads',
    valueType: 'number',
    importance: 'recommended',
    description: 'Maximum number of ads in the pod (a count)',
    value: { from: 'derived', source: 'pod_max_ads' },
  },
  /**
   * **`dur` is the pod's ceiling under another name** (#1095). Kept `optional` because
   * `pod_max_dur` above is the recommended spelling and seeding both would give one concept
   * two rows — but many exchanges spell it `dur`, so it stays offered.
   *
   * An earlier pass here described it as content length, which was truthful about the macro
   * and wrong about the concept: it made the catalog consistently wrong rather than right.
   */
  {
    name: 'dur',
    valueType: 'number',
    importance: 'optional',
    description: 'Maximum total pod duration (seconds)',
    value: { from: 'derived', source: 'pod_max_duration' },
  },
  {
    name: 'content_duration',
    valueType: 'number',
    importance: 'recommended',
    description: 'Content length (seconds)',
    value: { from: 'derived', source: 'content_duration' },
  },
  /**
   * **Deliberately untyped** (`#1191`). The resolver returns `Date.now()`, but a cache buster is
   * whatever makes the URL unique — a random token is as correct as a timestamp, and declaring
   * `number` would flag an operator's own `cb=a7f3c2` as wrong when it does the job perfectly.
   * An unstated type says *nothing*, which is the honest answer here rather than a missing one.
   */
  {
    name: 'cb',
    importance: 'optional',
    description: 'Cache buster / timestamp',
    value: { from: 'derived', source: 'cb' },
  },
];

/** OpenRTB carries these in the body — no URL-param catalog (guidance §14). */
export const ORTB_PARAM_CATALOG: ReadonlyArray<ParamCatalogField> = [];

/**
 * The catalog is keyed by the demand source's protocol, and takes the **typed**
 * `DemandSourceMethod` rather than a `string`.
 *
 * It used to be its own `SourceType = 'vast' | 'openrtb' | 'custom'` accepting
 * `string`, which is how #936 slipped past every gate: the console's protocol
 * values became `'VAST' | 'ORTB' | 'API'`, `type === 'openrtb'` stopped matching,
 * and every ORTB source silently started being offered the 12 recommended VAST
 * URL parameters — the exact thing the empty ORTB catalog exists to prevent
 * (guidance §14). Nothing failed, because a `string` parameter cannot be wrong.
 *
 * Keep the parameter typed. A mis-cased literal must be a compile error, not a
 * test that agrees with itself.
 */
export function paramCatalogFor(method: DemandSourceMethod | undefined): ReadonlyArray<ParamCatalogField> {
  return method === 'ORTB' ? ORTB_PARAM_CATALOG : VAST_PARAM_CATALOG;
}

/** Catalog fields not already present (case-insensitive) — the add-recommended set. */
export function getMissingParamFields(
  method: DemandSourceMethod | undefined,
  existingNames: ReadonlySet<string>,
): ReadonlyArray<ParamCatalogField> {
  const have = new Set(Array.from(existingNames, (n) => n.trim().toLowerCase()));
  return paramCatalogFor(method).filter((f) => !have.has(f.name));
}

/** The catalog entry for a param name, if any (case-insensitive). */
export function paramCatalogEntry(method: DemandSourceMethod | undefined, name: string): ParamCatalogField | undefined {
  const lower = name.trim().toLowerCase();
  return paramCatalogFor(method).find((f) => f.name === lower);
}

/**
 * **The names the driver fills from session context with no operator row** (`#951 · R5`).
 *
 * **Derived from the shared declaration since 2026-09-08** (`X33a`, `#1251`) — it was a hand-kept
 * mirror of sixteen names, on the reasoning that *the console cannot import `libs/*`*. That was
 * true when it was written and is not now: `auto-populated-params.ts` is aliased, and the alias is
 * what lets the console ask the **question** (*which concept does this row cover*) rather than only
 * compare **names**. A name set cannot see that `client_ip` covers `ip`, which is the case the
 * concept dedupe exists for.
 *
 * Kept as a named set rather than inlined because three surfaces ask *is this row auto-sent* and a
 * `Set` is what they want; what is gone is the second list.
 */
export const AUTO_POPULATED_PARAM_NAMES: ReadonlySet<string> = new Set(AUTO_POPULATED_VAST_PARAM_NAMES);
